import { Calendar, MapPin, Music, Mic2, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import homeData from '@/locales/home.json'
import eventData from '@/locales/event.json'
import globalData from '@/locales/global.json'

/**
 * Hero Section spécifique pour la page d'accueil du festival Rock'In Agde
 * Utilise les données JSON - Ne change que via les fichiers JSON
 */
export default function FestivalHeroSection() {
  const { hero, stats } = homeData
  const { nextEdition } = eventData
  const { site } = globalData

  return (
    <section id="hero" className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden pt-16 md:pt-20">
      {/* Background Image avec overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/hero/banner-concert.jpg" 
          alt="Rock'In Agde Concert" 
          className="w-full h-full object-cover"
        />
        {/* Overlay gradient noir pour lisibilité */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
      </div>

      {/* Contenu */}
      <div className="relative z-10 container-custom px-4 text-center text-white">
        {/* Badge édition */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6 animate-fade-in">
          <Music className="text-primary-400" size={20} />
          <span className="text-sm font-medium">{stats.edition} • {nextEdition.date}</span>
        </div>

        {/* Titre principal - Ultra impact avec Bebas Neue */}
        <h1 className="font-display text-6xl md:text-8xl lg:text-9xl font-bold text-primary-500 mb-6 tracking-wider drop-shadow-2xl animate-slide-up">
          {site.name}
        </h1>

        {/* Sous-titre élégant */}
        <p className="font-serif text-2xl md:text-4xl lg:text-5xl text-white mb-4 font-light animate-slide-up animation-delay-100">
          {hero.title}
        </p>

        {/* Description */}
        <p className="max-w-3xl mx-auto text-lg md:text-xl text-neutral-200 mb-8 leading-relaxed animate-fade-in animation-delay-200">
          {hero.description}
        </p>

        {/* Stats Cards */}
        <div className="flex flex-wrap justify-center gap-4 mb-10 animate-fade-in animation-delay-300">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-xl">
            <div className="flex items-center gap-2">
              <Users className="text-primary-400" size={20} />
              <span className="font-bold text-2xl">{stats.artists}</span>
              <span className="text-neutral-300">{hero.labels.artists}</span>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-xl">
            <div className="flex items-center gap-2">
              <Mic2 className="text-primary-400" size={20} />
              <span className="font-bold text-2xl">{stats.spectators}</span>
              <span className="text-neutral-300">{hero.labels.spectators}</span>
            </div>
          </div>
        </div>

        {/* Infos pratiques */}
        <div className="flex flex-wrap justify-center items-center gap-6 mb-10 text-neutral-200 animate-fade-in animation-delay-400">
          <div className="flex items-center gap-2">
            <Calendar className="text-primary-400" size={20} />
            <span className="font-medium">{nextEdition.date}</span>
          </div>
          <div className="h-1 w-1 rounded-full bg-neutral-400" />
          <div className="flex items-center gap-2">
            <MapPin className="text-primary-400" size={20} />
            <span className="font-medium">{nextEdition.location}</span>
          </div>
        </div>

        {/* CTA Principal */}
        <div className="flex flex-wrap justify-center gap-4 animate-fade-in animation-delay-500">
          <Link 
            to={hero.cta.link} 
            className="group inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-2xl hover:shadow-primary-500/50 hover:scale-105"
          >
            <Mic2 size={24} />
            {hero.cta.text}
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </Link>
          <a 
            href={nextEdition.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 border-2 border-white/30 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all"
          >
            <MapPin size={24} />
            {hero.locationButton}
          </a>
        </div>

        {/* Baseline */}
        <p className="mt-12 text-sm md:text-base text-neutral-300 italic max-w-2xl mx-auto animate-fade-in animation-delay-600">
          {site.baseline}
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full" />
        </div>
      </div>
    </section>
  )
}
